import { Component, EventEmitter, Output, signal, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import {
  Column,
  CustomAction,
  DataTableComponent,
  EmptyTableComponent,
  ErrorComponent,
  NimmstaDataSource,
  Organization,
  OrganizationService,
  SaveableFormComponent,
  StatusBadgePipe,
} from '@nimmsta/ng-commons-lib';
import { HttpErrorResponse } from '@angular/common/http';
import { Configuration } from '../../models/configuration';
import { ConfigurationService } from '../../services/configuration.service';
import { OrganizationNameService } from './organization-name.service';
import { TranslatePipe } from '@ngx-translate/core';

type SimpleOrganization = {
  id: string;
  name: string;
};

@Component({
  selector: 'app-configuration-org-form',
  templateUrl: 'configuration-org-form.component.html',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslatePipe,
    DataTableComponent,
    EmptyTableComponent,
    ErrorComponent,
    TranslatePipe,
  ],
  providers: [StatusBadgePipe],
})
export class ConfigurationOrgFormComponent extends SaveableFormComponent<Configuration> {
  private fb = inject(FormBuilder);
  private statusBadge = inject(StatusBadgePipe);
  private organizationNameService = inject(OrganizationNameService);

  protected override dataService = inject(ConfigurationService);

  @Output() override closeEvent = new EventEmitter();

  @Output() override loadingEvent = new EventEmitter<boolean>();

  organizationSource: NimmstaDataSource<Organization>;

  customActions: CustomAction<Organization>[] = [];

  selection: SimpleOrganization[] = [];

  organizations: SimpleOrganization[] = [];

  isModified = signal<boolean>(false);

  constructor() {
    const organizationService = inject(OrganizationService);

    super();

    this.setValue({} as Configuration);
    this.organizationSource = new NimmstaDataSource<Organization>(
      organizationService,
    );
  }

  get columns(): Column[] {
    return [
      new Column('name', 'organizations.name', { sortable: true }),
      new Column('description', 'organizations.description', {
        sortable: true,
      }),
      new Column('address', 'organizations.address', { sortable: true }),
      new Column('status', 'organizations.status', {
        pipe: this.statusBadge,
        sortable: true,
      }),
    ];
  }

  select(orgId: string) {
    const org = this.organizationSource.data?.content.find(
      (o) => o.id === orgId,
    );
    if (org) {
      this.selection.push(org);
      this.calculateIsModified();
    }
  }

  remove(organization: SimpleOrganization) {
    this.selection = this.selection.filter((o) => o.id !== organization.id);
    this.calculateIsModified();
  }

  private calculateIsModified() {
    if (this.organizations.length !== this.selection.length) {
      this.isModified.set(true);
    } else {
      this.isModified.set(
        this.organizations.some(
          (o) => !this.selection.find((s) => s.id === o.id),
        ),
      );
    }
  }

  save() {
    if (!this.isModified()) {
      this.closeEvent.emit();
      return;
    }
    this.dataService
      .updateOrganizationIds(
        this.value,
        this.selection.map((o) => o.id),
      )
      .subscribe({
        next: (configuration) => {
          this.setValue(configuration);
          this.closeEvent.emit();
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
        },
      });
  }

  protected setFormGroup(): void {
    this.form = this.fb.group({});
  }

  override setValue(value: Configuration) {
    super.setValue(value);
    if (value.organizationIds?.length) {
      this.organizationNameService.getNameMap(value.organizationIds).subscribe({
        next: (orgMap) => {
          this.organizations = value.organizationIds.map((id) => ({
            id,
            name: orgMap[id],
          }));
          // Create copy of organizations to avoid reference issues
          this.selection = this.organizations.slice();
          this.isModified.set(false);
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
        },
      });
    }
  }
}
